import axios from "axios";
import { Field, Form, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { NODE_API_URL, BASE_URL } from "../../../../env_config";
import Cookies from "js-cookie";
import {
    Button,
    CircularProgress,
    Grid,
    InputLabel,
    makeStyles,
} from "@material-ui/core";
import { toast } from "react-toastify";
import { InputField } from "../CcmodeSetupNew/FormFields";
import { useCCmodeSettingContext } from "../../../../context/ccmodeSettingContext";
import { useEffect } from "react";
import { isEqual } from "lodash";
import UnSavedChangesPopUp from "../../Popup/UnSavedChangesPopUp";

const Schema = Yup.object().shape({
    id: Yup.number().required("user id is required"),
    customer_id: Yup.number().required("customer id is required"),
    password: Yup.string()
        .required(`Password is Required`)
        .min(8, "Password must be at least 8 characters long.")
        .max(32, "Password cannot exceed 32 characters.")
        .matches(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@.#$&_*])[A-Za-z\d@.#$&_*]{8,32}$/,
            "Password must contain: Upper and lower case letters, numbers, and at least one special symbol." // Test Case ID 1
        ),
    confirmPassword: Yup.string()
        .oneOf([Yup.ref("password"), null], "Password must match")
        .required("Confirm Password is Required"),
});

const useStyles = makeStyles((theme) => ({
    button: {
        marginLeft: theme.spacing(2),
    },
    buttonProgress: {
        position: "absolute",
        top: "50%",
        left: "50%",
    },
    textField: {
        "& .MuiOutlinedInput-root": {
            borderRadius: "4px !important",
        },
        "& .MuiInputBase-input.Mui-disabled": {
            borderRadius: "4px",
            height: "0px",
        },
    },
}));

const CcmodeUpdatePassword = ({
    language,
    updatePassword,
    setUpdatePassword,
}) => {
    const INITIAL_UPDATE_PASS_OBJ = {
        id: updatePassword.id,
        customer_id: updatePassword.customer_id,
        password: "",
        confirmPassword: "",
    };

    const {
        activeKey,
        setIsFormChanged,
        showFormChangePopUp,
        setShowFormChangePopUp,
        nextKey,
        setNextKey,
        setActiveKey,
    } = useCCmodeSettingContext();

    const classes = useStyles();

    function handleSubmit(values, action) {
        axios({
            method: "POST",
            url: `${NODE_API_URL}/INTL/change-password`,
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${Cookies.get("cust_auth_token")}`,
            },
            data: values,
        })
            .then((res) => {
                if (res.data.success) {
                    toast.dismiss();
                    toast.success(res.data.message);
                    action.resetForm();
                    setUpdatePassword((prev) => ({
                        ...prev,
                        id: "",
                        customer_id: "",
                        password: "",
                        confirmPassword: "",
                        isOpen: false,
                    }));

                    handlePopUpOnSuccess()
                }
            })
            .catch((error) => {
                if (error?.response?.data?.message === "UnAuthorized!") {
                    var cookies = document.cookie.split(";");
                    for (var i = 0; i < cookies.length; i++) {
                        if (cookies[i] && !cookies[i].includes("view")) {
                            var equals = cookies[i].indexOf("=");
                            var name =
                                equals > -1
                                    ? cookies[i].substr(0, equals)
                                    : cookies[i];
                            document.cookie =
                                name +
                                "=;expires=Thu, 01 Jan 1970 00:00:00 GMT";
                        }
                    }

                    window.location.replace(`${BASE_URL}/logout`);
                }
                if (error?.response?.data?.message) {
                    toast.error(error?.response?.data?.message);
                }
            })
            .finally(() => {
                action.setSubmitting(false);
            });
    }

    const formik = useFormik({
        initialValues: INITIAL_UPDATE_PASS_OBJ,
        validationSchema: Schema,
        onSubmit: handleSubmit,
    });

    useEffect(() => {
        if (activeKey !== "ccmodeuserinvite") {
            setUpdatePassword((prev) => ({
                ...prev,
                isOpen: false,
            }));
        }
    }, [activeKey]);

    useEffect(() => {
        if (updatePassword.isOpen) {
            const isMatched = isEqual(INITIAL_UPDATE_PASS_OBJ, formik.values);

            if (!isMatched) {
                setIsFormChanged(true);
            } else {
                setIsFormChanged(false);
            }
        }
    }, [formik.values]);

    function handlePopUpOnSuccess() {
        setIsFormChanged(false);
        setShowFormChangePopUp(false);
        setNextKey(null);
    }

    return (
        <FormikProvider value={formik}>
            <Form>
                <Grid container spacing={3}>
                    <Grid item xs={12} className="my-6">
                        <Grid
                            container
                            spacing={3}
                            alignItems="flex-start"
                            justify="space-between"
                        >
                            <Grid item xs={12} md={4}>
                                <InputLabel className="font-size-h6 font-weight-bold text-capitalize">
                                    {language.CCMODE_INVITE_USER_EMAIL}
                                    <span className="small d-block mt-2">
                                        email of the user
                                    </span>
                                </InputLabel>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Field name="email">
                                    {({ field, meta }) => (
                                        <InputField
                                            {...field}
                                            className={classes.textField}
                                            fullWidth
                                            type="email"
                                            disabled={true}
                                            value={updatePassword.email}
                                            InputProps={{
                                                style: {
                                                    height: "40px",
                                                },
                                            }}
                                            error={
                                                meta.touched &&
                                                Boolean(meta.error)
                                            }
                                            helperText={
                                                meta.touched && meta.error
                                            }
                                        />
                                    )}
                                </Field>
                            </Grid>
                        </Grid>
                    </Grid>
                    <hr className="w-100" />
                    <Grid item xs={12} className="my-6">
                        <Grid
                            container
                            spacing={3}
                            alignItems="flex-start"
                            justify="space-between"
                        >
                            <Grid item xs={12} md={4}>
                                <InputLabel className="font-size-h6 font-weight-bold text-capitalize">
                                    {language.CCMODE_SETUP_LOGIN_FORM_PASSWORD}*
                                    <span className="small d-block mt-2">
                                        enter new password
                                    </span>
                                </InputLabel>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Field name="password">
                                    {({ field, meta }) => (
                                        <InputField
                                            {...field}
                                            className={classes.textField}
                                            fullWidth
                                            type="password"
                                            InputProps={{
                                                style: {
                                                    height: "40px",
                                                },
                                            }}
                                            error={
                                                meta.touched &&
                                                Boolean(meta.error)
                                            }
                                            helperText={
                                                meta.touched && meta.error
                                            }
                                        />
                                    )}
                                </Field>
                            </Grid>
                        </Grid>
                    </Grid>
                    <hr className="w-100" />
                    <Grid item xs={12} className="my-6">
                        <Grid
                            container
                            spacing={3}
                            alignItems="flex-start"
                            justify="space-between"
                        >
                            <Grid item xs={12} md={4}>
                                <InputLabel className="font-size-h6 font-weight-bold text-capitalize">
                                    {language.CCMODE_SETUP_LOGIN_FORM_CPASSWORD}
                                    *
                                    <span className="small d-block mt-2">
                                        confirm new password
                                    </span>
                                </InputLabel>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Field name="confirmPassword">
                                    {({ field, meta }) => (
                                        <InputField
                                            {...field}
                                            className={classes.textField}
                                            fullWidth
                                            type="password"
                                            InputProps={{
                                                style: {
                                                    height: "40px",
                                                },
                                            }}
                                            error={
                                                meta.touched &&
                                                Boolean(meta.error)
                                            }
                                            helperText={
                                                meta.touched && meta.error
                                            }
                                        />
                                    )}
                                </Field>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid
                    className="bg-white sticky-top"
                    container
                    justify={"space-between"}
                    alignItems="center"
                    style={{ bottom: "0px", paddingBottom: "18px" }}
                >
                    <Button
                        onClick={() =>
                            setUpdatePassword((prev) => ({
                                ...prev,
                                id: "",
                                customer_id: "",
                                password: "",
                                confirmPassword: "",
                                isOpen: false,
                            }))
                        }
                        className={`btn btn-danger`}
                    >
                        Cancel
                    </Button>
                    <Button
                        disabled={formik.isSubmitting}
                        type="submit"
                        variant="contained"
                        color="primary"
                        className={`${classes.button} btn`}
                    >
                        submit
                        {formik.isSubmitting && (
                            <CircularProgress
                                size={24}
                                className={classes.buttonProgress}
                            />
                        )}
                    </Button>
                </Grid>
            </Form>
            <UnSavedChangesPopUp
                show={
                    showFormChangePopUp &&
                    activeKey === "ccmodeuserinvite" &&
                    updatePassword.isOpen
                }
                onClose={() => {
                    formik.resetForm();
                    setIsFormChanged(false);
                    setShowFormChangePopUp(false);
                    if (nextKey) {
                        setActiveKey(nextKey);
                        setNextKey(null);
                    }
                    setUpdatePassword((prev) => ({
                        ...prev,
                        isOpen: false,
                    }));
                }}
                onCross={() => {
                    setShowFormChangePopUp(false);
                }}
                message={"you have unsaved changes in reset password form"}
                // saveBtn={"continue setting password"}
                handleSave={() => {
                    setShowFormChangePopUp(false);
                }}
            />
        </FormikProvider>
    );
};

export default CcmodeUpdatePassword;
